/**
 * Configurazione tema di default - SIMPLIFIED
 * Solo i campi essenziali per il theming
 */

import { ThemeConfig } from '../types/theme';

// Tailwind colors (kept for reference)
const colors = {
  navy: { 900: '#0B3D6B', 700: '#30546f' },
  primary: { 500: '#0ea5e9', 600: '#0284c7', 700: '#0369a1' },
  orange: { 500: '#F97316', 600: '#ea580c' },
};

export const defaultTheme: ThemeConfig = {
  mode: 'light',

  // Colors - essential fields only
  colors: {
    // Brand
    primary: colors.navy[900],
    primaryHover: colors.navy[700],
    secondary: colors.primary[500],
    secondaryHover: colors.primary[600],
    accent: colors.orange[500],
    accentHover: colors.orange[600],

    // Backgrounds
    background: '#ffffff',
    surface: '#f8fafc',
    surfaceElevated: '#ffffff',
    surfaceHover: '#f1f5f9',

    // Text
    text: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#64748b',
    textInverse: '#ffffff',

    // UI
    border: '#e2e8f0',
    borderLight: '#f1f5f9',
    divider: '#e2e8f0',

    // State
    success: '#22c55e',
    warning: '#f59e0b',
    error: '#ef4444',
    info: colors.primary[500],

    // iOS colors (for UIConfigurator)
    ios: {
      blue: '#007aff',
      green: '#34c759',
      red: '#ff3b30',
      orange: '#ff9500',
      yellow: '#ffcc00',
      gray: '#8e8e93',
      purple: '#af52de',
      pink: '#ff2d55',
      teal: '#5ac8fa',
      indigo: '#5856d6',
      lightGray: '#c7c7cc',
      darkGray: '#3a3a3c',
      systemBackground: '#f2f2f7',
      secondarySystemBackground: '#ffffff',
      tertiarySystemBackground: '#f2f2f7',
      separator: '#c6c6c8',
      fill: '#787880',
      white: '#ffffff',
    },

    // Status colors (for UIConfigurator)
    status: {
      online: '#22c55e',
      offline: '#ef4444',
      pending: '#f59e0b',
      available: '#22c55e',
      reserved: '#f59e0b',
      sold: '#ef4444',
      inTrattativa: '#8b5cf6',
    },
  },

  // Typography - font families and sizes
  typography: {
    fontFamily: 'Roboto, system-ui, sans-serif',
    fontFamilyHeading: 'Roboto, system-ui, sans-serif',
    fontFamilyMono: 'JetBrains Mono, monospace',
    baseFontSize: '16px',

    h1: { fontSize: '2.5rem', fontWeight: '700', lineHeight: '1.2', color: '#0B3D6B' },
    h2: { fontSize: '2rem', fontWeight: '600', lineHeight: '1.3', color: '#0B3D6B' },
    h3: { fontSize: '1.75rem', fontWeight: '600', lineHeight: '1.4', color: '#0B3D6B' },
    h4: { fontSize: '1.5rem', fontWeight: '500', lineHeight: '1.4', color: '#0B3D6B' },
    h5: { fontSize: '1.25rem', fontWeight: '500', lineHeight: '1.5', color: '#0B3D6B' },
    h6: { fontSize: '1rem', fontWeight: '500', lineHeight: '1.5', color: '#0B3D6B' },

    body: { fontSize: '1rem', fontWeight: '400', lineHeight: '1.6' },
    bodySmall: { fontSize: '0.875rem', fontWeight: '400', lineHeight: '1.5' },
    caption: { fontSize: '0.75rem', fontWeight: '400', lineHeight: '1.4' },

    // Card specific typography
    cardTitle: { fontSize: '1.125rem', fontWeight: '600', fontFamily: 'Roboto, sans-serif' },
    cardPrice: { fontSize: '1.5rem', fontWeight: '700', fontFamily: 'Roboto, sans-serif' },
    cardDescription: { fontSize: '0.875rem', fontWeight: '400', fontFamily: 'Roboto, sans-serif' },
  },

  // Navigation - essential settings
  navigation: {
    sidebar: {
      width: '280px',
      collapsedWidth: '80px',
      background: '#ffffff',
      borderRight: '#e2e8f0',
      // Menu items
      menuItemBackground: 'transparent',
      menuItemTextColor: '#374151',
      menuItemIconColor: '#6b7280',
      menuItemHoverBackground: '#f3f4f6',
      menuItemHoverTextColor: '#374151',
      menuItemHoverIconColor: '#0B3D6B',
      menuItemActiveBackground: 'rgba(11, 61, 107, 0.1)',
      menuItemActiveTextColor: '#0B3D6B',
      menuItemActiveIconColor: '#0B3D6B',
      // Section title
      sectionTitleColor: '#64748b',
      sectionTitleFontSize: '11px',
      // Padding
      padding: '12px 16px',
    },
    header: {
      height: '64px',
      background: '#ffffff',
      borderBottom: '#e2e8f0',
      iconColor: '#6b7280',
      textColor: '#374151',
    },
    bottomNav: {
      height: '64px',
      background: '#ffffff',
      borderTop: '#e2e8f0',
    },
  },

  // Glassmorphism - simplified core settings
  glass: {
    enabled: true,
    blur: 15,
    opacity: 0.1,
    background: 'rgba(255, 255, 255, 0.1)',
    borderOpacity: 0.15,
  },

  // Card - default card styles
  card: {
    background: '#ffffff',
    border: '#e2e8f0',
    borderRadius: '16px',
    padding: '16px',
    shadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
    shadowHover: '0 10px 15px -3px rgba(0,0,0,0.15)',
    titleColor: '#0f172a',
    subtitleColor: '#475569',
    textColor: '#64748b',
  },

  // Layout - container and grid settings
  layout: {
    maxWidth: '1400px',
    padding: '16px',
    gridGap: '16px',
    containerBackground: '#ffffff',
    containerBorder: '#e2e8f0',
  },

  // BoatCard - boat card component styles
  boatCard: {
    image: { borderRadius: '12px', aspectRatio: '16/9' },
    title: { fontSize: '18px', fontWeight: 600, color: '#0B3D6B', fontFamily: 'Roboto, sans-serif' },
    category: { fontSize: '10px', fontWeight: 600, color: '#0B3D6B', background: 'transparent', fontFamily: 'Roboto, sans-serif' },
    year: { fontSize: '12px', color: '#0B3D6B', fontFamily: 'Roboto, sans-serif' },
    optionals: { fontSize: '8px', fontWeight: 600, color: '#16a34a', background: '#f0fdf4', fontFamily: 'Roboto, sans-serif' },
    dealer: { fontSize: '10px', color: '#0B3D6B', fontFamily: 'Roboto, sans-serif' },
    specs: { fontSize: '9px', color: '#0B3D6B', fontFamily: 'Roboto, sans-serif' },
    engine: { fontSize: '9px', color: '#0B3D6B', fontFamily: 'Roboto, sans-serif' },
    price: { fontSize: '17px', fontWeight: 600, color: '#0B3D6B', fontFamily: 'Roboto, sans-serif' },
    retailPrice: { fontSize: '10px', color: '#999999', fontFamily: 'Roboto, sans-serif' },
    percentage: { fontSize: '10px', fontWeight: 500, color: '#f59e0b', fontFamily: 'Roboto, sans-serif' },
  },

  // Cards - card component styles (for UIConfigurator)
  cards: {
    container: {
      background: '#ffffff',
      border: '#e2e8f0',
      borderRadius: '12px',
      padding: '16px',
      shadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    hover: {
      shadow: '0 4px 6px rgba(0,0,0,0.1)',
      transform: 'scale-105',
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#000000',
    },
    subtitle: {
      fontSize: '14px',
      color: '#666666',
    },
  },

  // Forms - form input styles (for UIConfigurator)
  forms: {
    input: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '14px',
      color: '#000000',
      background: '#ffffff',
      border: '#e2e8f0',
      borderRadius: '8px',
      padding: '8px 12px',
    },
  },

  // Tables - table styles (for UIConfigurator)
  tables: {
    background: '#ffffff',
    border: '#e2e8f0',
    header: {
      background: '#f8fafc',
      borderBottom: '#e2e8f0',
    },
    row: {
      borderBottom: '#f1f5f9',
    },
  },

  // Modals - modal styles (for UIConfigurator)
  modals: {
    overlay: {
      background: 'rgba(0,0,0,0.5)',
      blur: '4px',
    },
    container: {
      background: '#ffffff',
      borderRadius: '12px',
      padding: '24px',
    },
  },

  // Charts - chart styles (for UIConfigurator)
  charts: {
    colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'],
    grid: {
      color: '#e5e7eb',
      width: '1px',
    },
  },

  // Animation - animation styles (for UIConfigurator)
  animation: {
    transition: {
      duration: '200ms',
      easing: 'ease-in-out',
    },
    hover: {
      scale: '1.05',
      duration: '200ms',
    },
  },

  // Background - background styles (for UIConfigurator)
  background: {
    light: '#f8fafc',
    dark: '#0f172a',
    text: {
      light: '#0f172a',
      dark: '#f8fafc',
    },
  },

  // Badges - badge styles (for UIConfigurator Badges section)
  badges: {
    typography: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '12px',
      fontWeight: '500',
    },
    default: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '12px',
      fontWeight: '500',
      color: '#374151',
      background: '#f3f4f6',
    },
    primary: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '12px',
      fontWeight: '500',
      color: '#ffffff',
      background: '#0B3D6B',
    },
    secondary: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '12px',
      fontWeight: '500',
      color: '#0ea5e9',
      background: '#e0f2fe',
    },
    success: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '12px',
      fontWeight: '500',
      color: '#22c55e',
      background: '#dcfce7',
    },
    warning: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '12px',
      fontWeight: '500',
      color: '#f59e0b',
      background: '#fef3c7',
    },
    error: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '12px',
      fontWeight: '500',
      color: '#ef4444',
      background: '#fee2e2',
    },
    info: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '12px',
      fontWeight: '500',
      color: '#0ea5e9',
      background: '#e0f2fe',
    },
    reservation: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '12px',
      fontWeight: '500',
      color: '#f59e0b',
      background: '#fef3c7',
    },
    available: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '12px',
      fontWeight: '500',
      color: '#22c55e',
      background: '#dcfce7',
    },
    sold: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '12px',
      fontWeight: '500',
      color: '#ef4444',
      background: '#fee2e2',
    },
  },

  // Buttons - button styles (for UIConfigurator Buttons section)
  buttons: {
    primary: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '14px',
      fontWeight: '500',
      color: '#ffffff',
      background: '#0B3D6B',
    },
    secondary: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '14px',
      fontWeight: '500',
      color: '#0B3D6B',
      background: '#ffffff',
      border: '#e2e8f0',
    },
    ghost: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '14px',
      fontWeight: '500',
      color: '#0B3D6B',
      background: 'transparent',
    },
    danger: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '14px',
      fontWeight: '500',
      color: '#ffffff',
      background: '#ef4444',
    },
  },
};

export default defaultTheme;
